import {useTranslation} from "react-i18next";
import { Button } from "primereact/button";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { DefectRectificationForm } from "../components/DefectRectification/DefectRectificationForm";
import { DefectRectificationDetails } from "../components/DefectRectification/DefectRectificationDetails";
import { errorToast, successToast } from "@idwal/idwal-react-components";
import { Card } from "primereact/card";
import { UnSavedChangesPrompt } from "../components/unsavedChangesPrompt/unsavedChangesPrompt";
import { DEFECTS, RECTIFICATIONS } from "../types/RouteNames";
import { sendGAEvent } from "../AnalyticsService";
import { ANALYTICS_BUTTON_CLICK, DEFECTS_RECTIFICATION_SAVE_CLICK } from "../constants/analytics";
import { DefectStatus } from "../types/DefectStatus";
import { Capability } from "../types/Capability";
import { useCapabilities } from "../hooks/useCapabilities";
import { DefectRectificationActivity } from "../components/DefectRectification/DefectRectificationActivity";
import { useAppDispatch, useAppSelector } from "../hooks/storeHooks";
import { selectRectification } from "../store/selectors/rectificationSelector";
import { createRectification, fetchRectification } from "../store/thunks/rectificationThunks";
import { ProgressSpinner } from "primereact/progressspinner";
import { selectDefectsLoading } from "../store/selectors/defectSelectors";
import { selectSharedDefectsLoading } from "../store/selectors/sharedDefectsSelectors";
import { Message } from "primereact/message";
import { DefectActivity } from "../types/DefectActivity";
import "./DefectRectification.css";
import { setShowEvidenceDialog } from "../store/slices/rectificationSlice";
import { ShowEvidenceDialog } from "../components/DefectRectification/ShowEvidenceDialog";

const DefectRectification = (props: {
    sharedDefect?: boolean;
}) => {
    const { sharedDefect } = props;

    const formRef = useRef();
    const dispatch = useAppDispatch();

    const { t } = useTranslation("locale");
    const navigate = useNavigate();
    const { defectId } = useParams();

    const {
        data: rectification,
        loading,
        error,
        defect,
        activityData,
        activityLoading,
        activityError,
        hasEvidence: hasAssets
    } = useAppSelector(selectRectification);
    const defectsLoading = useAppSelector(selectDefectsLoading);
    const sharedDefectsLoading = useAppSelector(selectSharedDefectsLoading);

    const dataLoading = sharedDefect ? sharedDefectsLoading : defectsLoading;

    const [saveLoading, setSaveLoading] = useState<boolean>(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    const [unsavedModalVisible, setUnsavedModalVisible] = useState<boolean>(false);
    const [latestCommentActivity, setLatestCommentActivity] = useState<DefectActivity | null>(null);

    useEffect(() => {
        if (!dataLoading) {
            dispatch(fetchRectification(defectId, sharedDefect));
        }
    }, [dataLoading]);

    useEffect(() => {
        if (activityData.length === 0) {
            return;
        }

        const commentActivity = activityData.filter((activity) => activity.defectComment);
        if (commentActivity.length === 0) {
            setLatestCommentActivity(null);
            return;
        }
        const latestComment = commentActivity.sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )[0];

        setLatestCommentActivity(latestComment);
    }, [activityData]);

    const { capabilities } = useCapabilities([
        Capability.CAP_FLEETAPP_CLOSE_DEFECTS
    ]);

    const navigateBack = () => {
        navigate(sharedDefect ? `/${RECTIFICATIONS}` : `/${DEFECTS}`);
    }

    const disabled = defect?.status === DefectStatus.closed && !capabilities[Capability.CAP_FLEETAPP_CLOSE_DEFECTS];

    if (!defect || loading) {
        return (
            <div className="flex h-screen justify-items-center align-items-center">
                <ProgressSpinner />
            </div>
        );
    }

    if (error) {
        errorToast(t("defectRectification.getError"));
    }

    /**
     * Disard changes, show popup if unsaved
     */
    const cancel = () => {
        if (hasUnsavedChanges) {
            setUnsavedModalVisible(true);
        }
        else {
            navigateBack();
        }
    }

    const handleOnSave = () => {
        const current = formRef.current as any;
        const formData = current.submitForm();

        const currentStatus = formData?.defectStatus ?? defect?.status;

        if (currentStatus === DefectStatus.rectified && !hasAssets) {
            dispatch(setShowEvidenceDialog(true));
        } else {
            save();
        }
    }

    /**
     * Save to defect rectification service and return back to table
     */
    const save = async () => {
        sendGAEvent(ANALYTICS_BUTTON_CLICK, DEFECTS_RECTIFICATION_SAVE_CLICK);
        
        if (formRef.current && defect) {
            const current = formRef.current as any;
            const formData = current.submitForm();

            setSaveLoading(true);

            const created = await dispatch(createRectification(formData, defect, sharedDefect));

            setSaveLoading(false);

            if (created) {
                if (defect.rectificationId) {
                    successToast(t("defectRectification.updateMessage"));
                } else {
                    successToast(t("defectRectification.createMessage"));
                }
                
                navigateBack();
            }
        }
    }

    const getCommentContent = () => {
        if (!defect || !latestCommentActivity?.defectComment) {
            return <></>;
        }
        
        const userMatch = latestCommentActivity.activity.match(/^Comment by (.+)$/);
        let bannerPart1;
        if (userMatch && userMatch[1] !== "undefined") {
            bannerPart1 = `${t("defectRectification.leadingTextName")}${userMatch[1]}`;
        } else {
            bannerPart1 = `${t("defectRectification.leadingTextNoName")}`;
        }
        
        const bannerText = `${bannerPart1}: ${latestCommentActivity?.defectComment || ""}`;
        return (
            <div id="comment-banner" className="flex justify-content-between align-items-center w-full">
                <div className="align-items-center flex">
                    <i className="comment-banner-warn-symbol pi pi-exclamation-triangle mr-2"></i>
                    <span>{bannerText}</span>
                </div>
                {/* <Button put back in when dismiss can be saved
                    icon="pi pi-times"
                    className="comment-banner-dismiss p-button-text p-button-plain"
                    onClick={() => setLatestCommentActivity(null)}
                    aria-label={t("defectRectification.dismiss")}
                /> */}
            </div>
        );
    }

    return (
        <div className="p-5 w-full">
            {!activityLoading && sharedDefect && latestCommentActivity && (defect?.status === DefectStatus.inProgress) && (
                <Message 
                    severity="warn" 
                    style={{width: "100%"}} 
                    className="mb-4 justify-content-start"
                    content={getCommentContent()}
                />
            )}
            <UnSavedChangesPrompt
                visible={unsavedModalVisible}
                setVisible={setUnsavedModalVisible}
                hasUnsavedChanges
                continueAction={navigateBack}
            />
            <div className="grid align-items-start justify-content-center flex-grow-1">
                <div className="col-12 lg:col-6">
                    <DefectRectificationDetails defect={defect} />
                    <DefectRectificationActivity 
                        className="mt-4" 
                        activityData={activityData} 
                        activityLoading={activityLoading} 
                        activityError={activityError}
                    />
                </div>

                <div className="col-12 lg:col-6">
                    <Card
                        header={
                            <h2
                                style={{
                                    border: "none",
                                    marginLeft: "0",
                                    whiteSpace: "nowrap"
                                }}
                            >
                                {t("defectRectification.title")}
                            </h2>
                        }
                    >
                        <DefectRectificationForm
                            fromRef={formRef}
                            defect={defect}
                            disabled={disabled}
                            rectificationDetails={rectification}
                            setHasUnsavedChanges={setHasUnsavedChanges}
                            sharedDefect={sharedDefect}
                        />
                    </Card>
                </div>
            </div>

            <div className="mt-6 w-full flex align-items-center justify-content-center gap-4">
                <Button
                    className="p-button p-component p-button-text"
                    label={t("defectRectification.cancel")}
                    data-cy="cancel"
                    onClick={cancel}
                />
                <Button
                    className="p-button p-button-primary"
                    label={t("defectRectification.save")}
                    data-cy="save"
                    loading={saveLoading}
                    onClick={handleOnSave}
                    disabled={disabled}
                />
            </div>
            <ShowEvidenceDialog onSave={save} />
        </div>
    )
}

export default DefectRectification;